/**
 * @file pages/pageWrapper.jsx
 * @description
 * This is the primary content wrapper for the Finch for Text minisite. To be
 * built out with auth checking (common to all pages), the global header, etc.
 */

import React, {Component} from 'react';
import { Switch, Route, Redirect } from 'react-router';
import history from '../utils/history';
import GoogleAnalytics from '../utils/googleAnalytics';
import Header from '../components/header/Header';
import session from '../utils/session';
import { BroadcastChannel } from 'broadcast-channel';
import moment from 'moment';
import Poller from '../utils/poller';
import urlHandler from '../utils/urlHandler';

import Login from './Login';
import Home from './Home';
import ApiDoc from '../components/documentation/apiDoc';
import HttpError from './HttpError';
import Demo from '../components/demo/FinchForTextControl';
import Knowledgebase from '../components/knowledgebase/KnowledgebaseControl';
import InterestAreas from '../components/interestareas/InterestAreasControl';
import Admin from '../components/customEntities/CustomEntityControl';
import Usage from '../components/usage/UsageControl';
import GeographicFeatureTypes from '../components/documentation/GeographicFeatureTypes';
import GeographicFeatureClasses from '../components/documentation/GeographicFeatureClasses';
import FIPSCodes from '../components/documentation/FIPSCodes';
import GENCCodes from '../components/documentation/GENCCodes';
import LanguageCodes from '../components/documentation/LanguageCodes';
import ContactUs from './ContactUs';
import ProviderCallback from './ProviderCallback';

/**
 * @class AuthRequiredRoute
 * @description Wrapper to check authentication before redirecting page
 */
 class AuthRequiredRoute extends Route {
    render() {
        if(!session.isAuthenticated()) {
            return <Redirect to="/login"></Redirect>
        } else {
            return <this.props.component />
        }
    }
}

class PageWrapper extends Component {

    constructor(props){
        super(props);

        this.updateSize = this.updateSize.bind(this);
        this.setupTokenRenewal = this.setupTokenRenewal.bind(this);

        this.channel = null;
        this.GA = new GoogleAnalytics();

        this.state = {
            contentHeight: window.innerHeight
        }

        this.tokenPoller = null;
    }

    componentDidMount(){

        window.addEventListener('resize', this.updateSize);

        // Initialize Google Analytics
        if (window._googleAnalyticsId) {
            this.GA.trackingId = window._googleAnalyticsId;
        }

        // Handle broadcast messages from other tabs.
        this.channel = new BroadcastChannel('fft_minisite');
        this.channel.addEventListener('message', (msg) => {
            if (msg && 'type' in msg) {
                if (msg.type === 'login') {
                    history.push('/');
                } else if (msg.type === 'logout') {
                    session.end();
                    if (this.tokenPoller) {
                        this.tokenPoller.stop();
                    }
                    history.push('/login');
                } else if (msg.type === 'login:provider') {
                    if (msg.data.expiresAt) {
                        this.setupTokenRenewal(msg.data.expiresAt);
                    }
                }
            }
        });

        history.listen((location) => {
            this.setState({
                location: location.pathname
            }, () => {

                // Handle Google Analytics pageview
                this.GA.pageview(location);

                var { hash } = location;

                // Push onto callback queue so it runs after the DOM is updated,
                // this is required when navigating from a different page so that
                // the element is rendered on the page before trying to getElementById.
                setTimeout(() => {
                    const id = hash.replace('#', '');
                    const element = document.getElementById(id);
                    if (element) {
                        element.scrollIntoView();
                    } else {
                        window.scrollTo(0,0);
                    }
                }, 0);
            })

            this.updateSize();
        }); 

        this.updateSize();
    }

    updateSize() {
        var contentHeight = window.innerHeight;
        if (history.location.pathname != '/login' && history.location.pathname != '/' && !history.location.pathname.includes('/callback')) {
            contentHeight -= 70;
        }
        this.setState({
            contentHeight
        })
    }

    componentWillUnmount() {
        console.log('UNMOUNTING');
        window.removeEventListener('resize', this.updateSize);
        this.channel.close();
    }

    /**
     * @function setupTokenRenewal
     * @description If provider login contains a expiresAt token, setup a poller to handle renewal of token
     * @param {string} expiresAt - Time/Date stamp of token expiration
     */
    setupTokenRenewal(expiresAt) {
        let now = moment();
        let expires = moment(expiresAt).subtract(30, 'seconds');
        let pollerMs = expires.diff(now);


        if (this.tokenPoller) {
            this.tokenPoller.stop();
        }

        this.tokenPoller = new Poller(() => {
            urlHandler.authenticateRenewToken((response, status) => {
                if (status === 200) {
                    console.log('Token renewal => ', response);
                    now = moment();
                    expires = moment(response.expiresAt).subtract(30, 'seconds');
                    if (this.tokenPoller) {
                        this.tokenPoller.interval = expires.diff(now);
                    }
                    session.setToken(response.token);
                } else {
                    console.log('Token renewal failed. Logging out. => ', response);
                    session.end();
                    if (this.tokenPoller) {
                        this.tokenPoller.stop();
                    }
                    history.push('/login');
                }
            });
            return true;
        }, pollerMs);
        this.tokenPoller.start();
    }

    render(){
        let isCallback = history.location.pathname.includes('/callback');
        return (
            <div className="app-wrapper">
                {!isCallback &&
                    <Header/>
                }
                <div className="content-wrapper" style={{height: this.state.contentHeight}}>
                    <Switch>
                        <Route path="/login"><Login onLogin={this.onLogin}/></Route>
                        <Route path="/error/:status/:message" component={HttpError}/>
                        <Route path="/documentation" component={ApiDoc}/>
                        <Route path="/callback/:protocol/:provider" component={ProviderCallback}/>
                        <Route path="/contact" component={ContactUs}/>
                        <AuthRequiredRoute path="/demo" component={Demo}/>
                        <AuthRequiredRoute path="/knowledgebase" component={Knowledgebase}/>
                        <AuthRequiredRoute path="/interestareas" component={InterestAreas}/>
                        <AuthRequiredRoute path="/admin" component={Admin}/>
                        <AuthRequiredRoute path="/usage" component={Usage}/>
                        <AuthRequiredRoute path="/dsg_codes" component={GeographicFeatureTypes}/>
                        <AuthRequiredRoute path="/dsg_classes" component={GeographicFeatureClasses}/>
                        <AuthRequiredRoute path="/fips_codes" component={FIPSCodes}/>
                        <AuthRequiredRoute path="/genc_codes" component={GENCCodes}/>
                        <AuthRequiredRoute path="/lang_codes/:codes/:title" component={LanguageCodes}/>
                        <AuthRequiredRoute path="/lang_codes" component={LanguageCodes}/>
                        <AuthRequiredRoute path="/" component={Home}/>
                    </Switch>
                </div>
            </div>
        )
    };
};

export default PageWrapper;